import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IRootState } from "../../app/store";

export type IStandardRoomViewMode = "table" | "viewer";

const LS_KEYS = {
  viewMode: "standardRoom.viewMode",
  showOnlyCeilingComponents: "standardRoom.showOnlyCeilingComponents",
  showLengthComponents: "standardRoom.showLengthComponents",
  showFurniture: "standardRoom.showFurniture",
} as const;

const loadBoolean = (key: string, fallback: boolean): boolean => {
  const value = localStorage.getItem(key);
  if (value == null) return fallback;
  return value === "true";
};

const loadViewMode = (): IStandardRoomViewMode => {
  const value = localStorage.getItem(LS_KEYS.viewMode);
  return value === "viewer" ? "viewer" : "table";
};

export interface IStandardRoomSliceState {
  viewMode: IStandardRoomViewMode;
  showOnlyCeilingComponents: boolean;
  showLengthComponents: boolean;
  showFurniture: boolean;
}

const initialState: IStandardRoomSliceState = {
  viewMode: loadViewMode(),
  showOnlyCeilingComponents: loadBoolean(LS_KEYS.showOnlyCeilingComponents, false),
  showLengthComponents: loadBoolean(LS_KEYS.showLengthComponents, false),
  showFurniture: loadBoolean(LS_KEYS.showFurniture, true),
};

export const standardRoomSlice = createSlice({
  name: "standardRoom",
  initialState,
  reducers: {
    setStandardRoomViewMode: (state, action: PayloadAction<IStandardRoomViewMode>) => {
      localStorage.setItem(LS_KEYS.viewMode, action.payload);
      state.viewMode = action.payload;
    },
    setStandardRoomShowOnlyCeilingComponents: (state, action: PayloadAction<boolean>) => {
      localStorage.setItem(LS_KEYS.showOnlyCeilingComponents, String(action.payload));
      state.showOnlyCeilingComponents = action.payload;
    },
    setStandardRoomShowLengthComponents: (state, action: PayloadAction<boolean>) => {
      localStorage.setItem(LS_KEYS.showLengthComponents, String(action.payload));
      state.showLengthComponents = action.payload;
    },
    setStandardRoomShowFurniture: (state, action: PayloadAction<boolean>) => {
      localStorage.setItem(LS_KEYS.showFurniture, String(action.payload));
      state.showFurniture = action.payload;
    },
  },
});

export const {
  setStandardRoomViewMode,
  setStandardRoomShowOnlyCeilingComponents,
  setStandardRoomShowLengthComponents,
  setStandardRoomShowFurniture,
} = standardRoomSlice.actions;

export const selectStandardRoomViewMode = (state: IRootState): IStandardRoomViewMode =>
  state.standardRoom.viewMode;

export const selectStandardRoomShowOnlyCeilingComponents = (state: IRootState): boolean =>
  state.standardRoom.showOnlyCeilingComponents;

export const selectStandardRoomShowLengthComponents = (state: IRootState): boolean =>
  state.standardRoom.showLengthComponents;

export const selectStandardRoomShowFurniture = (state: IRootState): boolean =>
  state.standardRoom.showFurniture;
